/**
 * RichTextBox — contentEditable text box with inline @ mention chips.
 *
 * Provides a shared rich text component that replaces plain <textarea>
 * inputs across the canvas. Supports inline @ mention chips with a
 * dropdown, keyboard navigation, `@[[nodeId]]` serialization, and
 * an imperative handle for programmatic control.
 *
 * Fixes the cursor-jump-to-end bug present in V1 textarea inputs.
 */

import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import type { Node as ReactFlowNode } from 'reactflow'
import { buildCandidates, type MentionCandidate } from './mentionUtils'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import {
  createMentionChipElement,
  serializeNodeText,
  getPlainTextFromEditor,
  getReferencedNodeIdsFromEditor,
  selectionInsideEditor,
  placeCaretAtEnd,
  hydrateContent,
  DropdownItem,
} from '../../../chat/contentEditableUtils'

// ─── Types ───────────────────────────────────────────────────────────────────

export interface RichTextBoxState {
  plainText: string
  referencedNodeIds: string[]
}

export interface RichTextBoxHandle {
  clear: () => void
  focus: () => void
  setText: (text: string) => void
  getPlainText: () => string
  getReferencedNodeIds: () => string[]
  insertAtCursor: (text: string) => void
  wrapSelection: (before: string, after: string) => void
}

export interface RichTextBoxProps {
  /** Canvas nodes to build mention candidates from */
  canvasNodes?: ReactFlowNode[]
  /** Whether the text box is disabled */
  disabled?: boolean
  /** Placeholder text shown when empty */
  placeholder?: string
  /** Raw text containing @[[nodeId]] markers to hydrate into chips on mount */
  initialContent?: string
  /** Called when content changes */
  onContentChange?: (state: RichTextBoxState) => void
  /** Called when Enter (without Shift) is pressed (non-dropdown context) */
  onSubmit?: () => void
  /** Called when Escape is pressed (non-dropdown context) */
  onEscape?: () => void
  /** Called when a mention chip is clicked (to pan/zoom canvas to that node) */
  onMentionClick?: (nodeId: string) => void
  /** Additional CSS classes for the editor div */
  className?: string
  /** Additional CSS classes for the outer border container */
  containerClassName?: string
  /** data-testid for the outer border container */
  containerTestId?: string
  /** Min height in px. Default: 24 */
  minHeight?: number
  /** Max height in px. Default: 160 */
  maxHeight?: number
  /** Max character count for built-in counter. Default: 1000. Set to 0 to hide counter. */
  maxLength?: number
}

// ─── RichTextBox Component ───────────────────────────────────────────────────

const RichTextBoxInner = forwardRef<RichTextBoxHandle, RichTextBoxProps>(
  function RichTextBox(
    {
      canvasNodes,
      disabled = false,
      placeholder = 'Type a message...',
      initialContent,
      onContentChange,
      onSubmit,
      onEscape,
      onMentionClick,
      className = '',
      containerClassName,
      containerTestId,
      minHeight = 24,
      maxHeight = 160,
      maxLength = 1000,
    },
    ref
  ) {
    const projectId = useProjectId()
    const editorRef = useRef<HTMLDivElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const anchorRef = useRef<{ node: globalThis.Node; offset: number } | null>(null)
    const composingRef = useRef(false)
    const initialContentHydratedRef = useRef(false)
    const onContentChangeRef = useRef(onContentChange)
    onContentChangeRef.current = onContentChange

    const [showDropdown, setShowDropdown] = useState(false)
    const [filterText, setFilterText] = useState('')
    const [highlightIndex, setHighlightIndex] = useState(0)
    const [textLength, setTextLength] = useState(0)
    const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null)

    const candidates = useMemo(
      () => (canvasNodes ? buildCandidates(canvasNodes) : []),
      [canvasNodes]
    )

    const filteredCandidates = useMemo(() => {
      if (!filterText) return candidates
      const query = filterText.toLowerCase()
      return candidates.filter(
        (c) =>
          c.label.toLowerCase().includes(query) ||
          c.type.toLowerCase().includes(query)
      )
    }, [candidates, filterText])

    // ── Editor utilities ──

    const adjustHeight = useCallback(() => {
      const editor = editorRef.current
      if (!editor) return
      editor.style.height = 'auto'
      const nextHeight = Math.min(Math.max(editor.scrollHeight, minHeight), maxHeight)
      editor.style.height = `${nextHeight}px`
    }, [minHeight, maxHeight])

    const emitContentState = useCallback(() => {
      const editor = editorRef.current
      const plainText = getPlainTextFromEditor(editor)
      const state: RichTextBoxState = {
        plainText,
        referencedNodeIds: getReferencedNodeIdsFromEditor(editor),
      }
      setTextLength(plainText.length)
      onContentChangeRef.current?.(state)
      return state
    }, [])

    const closeDropdown = useCallback(() => {
      anchorRef.current = null
      setShowDropdown(false)
      setFilterText('')
      setHighlightIndex(0)
      setDropdownPos(null)
    }, [])

    // ── Mention trigger detection ──

    const syncMentionTrigger = useCallback(() => {
      const editor = editorRef.current
      if (!editor || candidates.length === 0 || composingRef.current) {
        closeDropdown()
        return
      }

      const selection = window.getSelection()
      if (!selectionInsideEditor(editor, selection) || !selection || selection.rangeCount === 0) {
        closeDropdown()
        return
      }

      const range = selection.getRangeAt(0)
      const textNode = range.startContainer
      if (textNode.nodeType !== Node.TEXT_NODE) {
        closeDropdown()
        return
      }

      const text = textNode.textContent ?? ''
      const cursorPos = range.startOffset

      let atPos = -1
      for (let i = cursorPos - 1; i >= 0; i--) {
        const char = text[i]
        if (char === '@') {
          atPos = i
          break
        }
        if (char === ' ' || char === '\n' || char === '\r') break
      }

      if (atPos >= 0 && (atPos === 0 || /\s/.test(text[atPos - 1] ?? ''))) {
        anchorRef.current = { node: textNode, offset: atPos }
        const query = text.substring(atPos + 1, cursorPos)
        setFilterText(query)
        setShowDropdown(true)
        setHighlightIndex(0)

        // Compute dropdown position from editor bounding rect
        const editorRect = editor.getBoundingClientRect()
        setDropdownPos({
          top: editorRect.top,
          left: editorRect.left,
          width: editorRect.width,
        })

        // If no candidates match, close the dropdown
        const queryLower = query.toLowerCase()
        const hasMatch = queryLower === '' || candidates.some(
          (c) => c.label.toLowerCase().includes(queryLower) || c.type.toLowerCase().includes(queryLower)
        )
        if (!hasMatch) {
          closeDropdown()
        }
        return
      }

      closeDropdown()
    }, [candidates, closeDropdown])

    // ── Chip operations ──

    const removeChip = useCallback(
      (chip: HTMLElement) => {
        chip.remove()
        const editor = editorRef.current
        if (editor && editor.textContent === '') {
          editor.innerHTML = ''
        }
        emitContentState()
        adjustHeight()
        if (editor) placeCaretAtEnd(editor)
      },
      [adjustHeight, emitContentState]
    )

    const insertMention = useCallback(
      (candidate: MentionCandidate) => {
        const editor = editorRef.current
        const anchor = anchorRef.current
        if (!editor || !anchor) {
          closeDropdown()
          return
        }

        const anchorNode = anchor.node
        if (anchorNode.nodeType !== Node.TEXT_NODE || !anchorNode.parentNode) {
          closeDropdown()
          return
        }

        const fullText = anchorNode.textContent ?? ''
        const mentionLength = 1 + filterText.length
        const before = fullText.substring(0, anchor.offset)
        const after = fullText.substring(anchor.offset + mentionLength)
        const parent = anchorNode.parentNode

        const beforeNode = document.createTextNode(before)
        const chip = createMentionChipElement(candidate, projectId)
        const trailingSpacer = after.length === 0 || /^\s/.test(after) ? '' : ' '
        const afterNode = document.createTextNode(`${trailingSpacer}${after}`)

        parent.insertBefore(beforeNode, anchorNode)
        parent.insertBefore(chip, anchorNode)
        parent.insertBefore(afterNode, anchorNode)
        parent.removeChild(anchorNode)

        // Place cursor after chip (no jump-to-end)
        const selection = window.getSelection()
        if (selection) {
          const range = document.createRange()
          range.setStart(afterNode, trailingSpacer.length)
          range.collapse(true)
          selection.removeAllRanges()
          selection.addRange(range)
        }

        emitContentState()
        adjustHeight()
        closeDropdown()
        editor.focus()
      },
      [adjustHeight, closeDropdown, emitContentState, filterText, projectId]
    )

    // ── Input/key handlers ──

    const handleInput = useCallback(() => {
      if (composingRef.current) return
      initialContentHydratedRef.current = true
      emitContentState()
      adjustHeight()
      syncMentionTrigger()
    }, [adjustHeight, emitContentState, syncMentionTrigger])

    const handleCompositionStart = useCallback(() => {
      composingRef.current = true
    }, [])

    const handleCompositionEnd = useCallback(() => {
      composingRef.current = false
      // After composition ends, check for @ trigger
      handleInput()
    }, [handleInput])

    // ── Shared serialization helper for copy/cut ──

    const serializeSelection = useCallback((): string | null => {
      const selection = window.getSelection()
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return null

      const range = selection.getRangeAt(0)
      const fragment = range.cloneContents()
      const container = document.createElement('div')
      container.appendChild(fragment)

      const serialized = serializeNodeText(container, container)
      return serialized || null
    }, [])

    // ── Copy handler: serialize chips as @[[nodeId]] ──

    const handleCopy = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
      const serialized = serializeSelection()
      if (!serialized) return

      e.preventDefault()
      e.clipboardData.setData('text/plain', serialized)
    }, [serializeSelection])

    // ── Cut handler: serialize chips then delete selected content ──

    const handleCut = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
      const serialized = serializeSelection()
      if (!serialized) return

      e.preventDefault()
      e.clipboardData.setData('text/plain', serialized)

      // Delete the selected content
      const selection = window.getSelection()
      if (selection && selection.rangeCount > 0) {
        selection.getRangeAt(0).deleteContents()
      }

      // Clean up empty editor state
      const editor = editorRef.current
      if (editor && editor.textContent === '') {
        editor.innerHTML = ''
      }

      emitContentState()
      adjustHeight()
    }, [adjustHeight, emitContentState, serializeSelection])

    // ── Paste handler: hydrate @[[nodeId]] markers into chips ──

    const handlePaste = useCallback(
      (e: React.ClipboardEvent<HTMLDivElement>) => {
        e.preventDefault()

        const editor = editorRef.current
        if (!editor) return

        const text = e.clipboardData.getData('text/plain')
        if (!text) return

        const selection = window.getSelection()
        if (!selection || selection.rangeCount === 0) return

        const range = selection.getRangeAt(0)
        if (!range.collapsed) {
          range.deleteContents()
        }

        const MARKER_RE = /@\[\[([^\]]+)\]\]/g
        const hasMarkers = MARKER_RE.test(text)

        if (hasMarkers) {
          // Build a document fragment with chips hydrated from markers
          MARKER_RE.lastIndex = 0
          const frag = document.createDocumentFragment()
          let lastIndex = 0
          let match: RegExpExecArray | null

          while ((match = MARKER_RE.exec(text)) !== null) {
            if (match.index > lastIndex) {
              frag.appendChild(document.createTextNode(text.slice(lastIndex, match.index)))
            }
            const nodeId = match[1]
            const candidate = candidates.find((c) => c.id === nodeId)
            if (candidate) {
              frag.appendChild(createMentionChipElement(candidate, projectId))
            } else {
              // Unknown node — render fallback chip with muted style
              const fallback: MentionCandidate = {
                id: nodeId,
                type: 'unknown',
                label: nodeId.slice(0, 8) + '...',
                icon: '',
              }
              const chip = createMentionChipElement(fallback, projectId)
              chip.className = chip.className
                .replace('border-primary/25', 'border-white/10')
                .replace('bg-primary/15', 'bg-white/5')
                .replace('text-primary', 'text-foreground/40')
              frag.appendChild(chip)
            }
            lastIndex = match.index + match[0].length
          }
          if (lastIndex < text.length) {
            frag.appendChild(document.createTextNode(text.slice(lastIndex)))
          }

          // Track the last node for cursor placement (insertNode empties the fragment)
          const lastChild = frag.lastChild

          range.insertNode(frag)

          // Move cursor after the last inserted node
          if (lastChild) {
            range.setStartAfter(lastChild)
            range.collapse(true)
          } else {
            range.collapse(false)
          }
          selection.removeAllRanges()
          selection.addRange(range)
        } else {
          // Plain text paste — insert as text node
          const textNode = document.createTextNode(text)
          range.insertNode(textNode)
          range.setStartAfter(textNode)
          range.collapse(true)
          selection.removeAllRanges()
          selection.addRange(range)
        }

        emitContentState()
        adjustHeight()
      },
      [adjustHeight, candidates, emitContentState, projectId]
    )

    const removeAdjacentChip = useCallback(
      (direction: 'backward' | 'forward') => {
        const editor = editorRef.current
        if (!editor) return false

        const selection = window.getSelection()
        if (!selectionInsideEditor(editor, selection) || !selection || selection.rangeCount === 0) {
          return false
        }

        const range = selection.getRangeAt(0)
        if (!range.collapsed) return false

        let adjacentNode: globalThis.Node | null = null
        if (range.startContainer.nodeType === Node.TEXT_NODE) {
          const textNode = range.startContainer as Text
          if (direction === 'backward' && range.startOffset === 0) {
            adjacentNode = textNode.previousSibling
          } else if (
            direction === 'forward' &&
            range.startOffset === (textNode.textContent ?? '').length
          ) {
            adjacentNode = textNode.nextSibling
          }
        } else if (range.startContainer.nodeType === Node.ELEMENT_NODE) {
          const container = range.startContainer as HTMLElement
          const childIndex =
            direction === 'backward' ? range.startOffset - 1 : range.startOffset
          adjacentNode = container.childNodes[childIndex] ?? null
        }

        if (
          adjacentNode instanceof HTMLElement &&
          adjacentNode.dataset.mentionPill === 'true'
        ) {
          removeChip(adjacentNode)
          return true
        }

        return false
      },
      [removeChip]
    )

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        // Dropdown keyboard nav
        if (showDropdown && filteredCandidates.length > 0) {
          if (event.key === 'ArrowDown') {
            event.preventDefault()
            setHighlightIndex((prev) => (prev + 1) % filteredCandidates.length)
            return
          }
          if (event.key === 'ArrowUp') {
            event.preventDefault()
            setHighlightIndex((prev) =>
              (prev - 1 + filteredCandidates.length) % filteredCandidates.length
            )
            return
          }
          if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault()
            insertMention(filteredCandidates[highlightIndex])
            return
          }
          if (event.key === 'Escape') {
            event.preventDefault()
            closeDropdown()
            return
          }
        }

        // Chip removal via Backspace/Delete
        if (event.key === 'Backspace' && removeAdjacentChip('backward')) {
          event.preventDefault()
          return
        }
        if (event.key === 'Delete' && removeAdjacentChip('forward')) {
          event.preventDefault()
          return
        }

        // Submit on Enter (no shift, no dropdown)
        if (event.key === 'Enter' && !event.shiftKey && !showDropdown) {
          event.preventDefault()
          onSubmit?.()
          return
        }

        // Escape (no dropdown)
        if (event.key === 'Escape' && !showDropdown) {
          event.preventDefault()
          onEscape?.()
        }
      },
      [
        closeDropdown,
        filteredCandidates,
        highlightIndex,
        insertMention,
        onEscape,
        onSubmit,
        removeAdjacentChip,
        showDropdown,
      ]
    )

    // ── Click handling for chips ──

    const handleEditorClick = useCallback(
      (event: MouseEvent) => {
        const target = event.target as HTMLElement | null
        if (!target) return

        // Remove button click
        const removeButton = target.closest('[data-mention-chip-remove]') as HTMLElement | null
        if (removeButton) {
          event.preventDefault()
          event.stopPropagation()
          const chip = removeButton.closest('[data-mention-pill]') as HTMLElement | null
          if (chip) removeChip(chip)
          return
        }

        // Chip label click — pan to node
        const chipLabel = target.closest('[data-mention-chip-label]') as HTMLElement | null
        if (chipLabel) {
          const chip = chipLabel.closest('[data-mention-pill]') as HTMLElement | null
          if (chip?.dataset.nodeId) {
            event.preventDefault()
            event.stopPropagation()
            onMentionClick?.(chip.dataset.nodeId)
          }
        }
      },
      [onMentionClick, removeChip]
    )

    // ── Imperative handle ──

    useImperativeHandle(
      ref,
      () => ({
        clear: () => {
          const editor = editorRef.current
          if (!editor) return
          editor.innerHTML = ''
          closeDropdown()
          emitContentState()
          adjustHeight()
        },
        focus: () => {
          const editor = editorRef.current
          if (!editor) return
          placeCaretAtEnd(editor)
        },
        setText: (text: string) => {
          const editor = editorRef.current
          if (!editor) return
          // Hydrate @[[nodeId]] markers
          hydrateContent(editor, text, candidates, projectId)
          emitContentState()
          adjustHeight()
        },
        getPlainText: () => getPlainTextFromEditor(editorRef.current),
        getReferencedNodeIds: () => getReferencedNodeIdsFromEditor(editorRef.current),
        insertAtCursor: (text: string) => {
          const editor = editorRef.current
          if (!editor) return
          editor.focus()
          const selection = window.getSelection()
          if (!selection || !selectionInsideEditor(editor, selection) || selection.rangeCount === 0) {
            placeCaretAtEnd(editor)
          }
          const sel = window.getSelection()
          if (!sel || sel.rangeCount === 0) return
          const range = sel.getRangeAt(0)

          if (!range.collapsed) {
            range.deleteContents()
          }

          const textNode = document.createTextNode(text)
          range.insertNode(textNode)

          range.setStartAfter(textNode)
          range.collapse(true)
          sel.removeAllRanges()
          sel.addRange(range)

          emitContentState()
          adjustHeight()
        },
        wrapSelection: (before: string, after: string) => {
          const editor = editorRef.current
          if (!editor) return
          editor.focus()
          const selection = window.getSelection()
          if (!selection || !selectionInsideEditor(editor, selection) || selection.rangeCount === 0) {
            placeCaretAtEnd(editor)
            const sel2 = window.getSelection()
            if (!sel2 || sel2.rangeCount === 0) return
            const range = sel2.getRangeAt(0)
            const textNode = document.createTextNode(before + after)
            range.insertNode(textNode)
            const cursorRange = document.createRange()
            cursorRange.setStart(textNode, before.length)
            cursorRange.collapse(true)
            sel2.removeAllRanges()
            sel2.addRange(cursorRange)
            emitContentState()
            adjustHeight()
            return
          }
          const range = selection.getRangeAt(0)

          if (range.collapsed) {
            const textNode = document.createTextNode(before + after)
            range.insertNode(textNode)
            const cursorRange = document.createRange()
            cursorRange.setStart(textNode, before.length)
            cursorRange.collapse(true)
            selection.removeAllRanges()
            selection.addRange(cursorRange)
          } else {
            const fragment = range.extractContents()
            const beforeNode = document.createTextNode(before)
            const afterNode = document.createTextNode(after)

            const wrapper = document.createDocumentFragment()
            wrapper.appendChild(beforeNode)
            wrapper.appendChild(fragment)
            wrapper.appendChild(afterNode)

            range.insertNode(wrapper)

            const cursorRange = document.createRange()
            cursorRange.setStartBefore(afterNode)
            cursorRange.collapse(true)
            selection.removeAllRanges()
            selection.addRange(cursorRange)
          }

          emitContentState()
          adjustHeight()
        },
      }),
      [adjustHeight, candidates, closeDropdown, emitContentState, projectId]
    )

    // ── Hydration of initial content ──

    useEffect(() => {
      const editor = editorRef.current
      if (!editor || !initialContent || initialContentHydratedRef.current) return
      // Wait for candidates if content has markers and canvasNodes not yet loaded
      const hasMarkers = /@\[\[[^\]]+\]\]/.test(initialContent)
      if (hasMarkers && canvasNodes === undefined) return
      initialContentHydratedRef.current = true

      hydrateContent(editor, initialContent, candidates, projectId)
      emitContentState()
      adjustHeight()
    }, [initialContent, canvasNodes, candidates, emitContentState, adjustHeight, projectId])

    // ── Event listeners ──

    useEffect(() => {
      const handleDocumentClick = (event: MouseEvent) => {
        const editor = editorRef.current
        const dropdown = dropdownRef.current
        const target = event.target as globalThis.Node | null
        if (!editor || !target) return
        if (!editor.contains(target) && !(dropdown && dropdown.contains(target))) {
          closeDropdown()
        }
      }

      const editor = editorRef.current
      if (!editor) return

      editor.addEventListener('click', handleEditorClick)
      document.addEventListener('mousedown', handleDocumentClick)
      return () => {
        editor.removeEventListener('click', handleEditorClick)
        document.removeEventListener('mousedown', handleDocumentClick)
      }
    }, [closeDropdown, handleEditorClick])

    // Scroll highlighted item into view
    useEffect(() => {
      if (!showDropdown || !dropdownRef.current) return
      const items = dropdownRef.current.querySelectorAll('[data-mention-item]')
      items[highlightIndex]?.scrollIntoView({ block: 'nearest' })
    }, [highlightIndex, showDropdown])

    return (
      <div className={`relative flex flex-col ${containerClassName ?? ''}`} data-testid={containerTestId}>
        {/* Dropdown — rendered via portal to avoid clipping by overflow containers */}
        {showDropdown && filteredCandidates.length > 0 && dropdownPos && createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[9999] max-h-[200px] overflow-y-auto rounded-lg border border-border-subtle bg-surface-1 shadow-lg"
            style={{
              top: dropdownPos.top - 4,
              left: dropdownPos.left,
              width: dropdownPos.width,
              transform: 'translateY(-100%)',
            }}
            role="listbox"
            data-testid="mention-dropdown"
          >
            {filteredCandidates.map((candidate, index) => (
              <DropdownItem
                key={candidate.id}
                candidate={candidate}
                isHighlighted={index === highlightIndex}
                projectId={projectId}
                onSelect={insertMention}
                onMouseEnter={() => setHighlightIndex(index)}
              />
            ))}
          </div>,
          document.body
        )}

        {/* Editor */}
        <div
          ref={editorRef}
          contentEditable={disabled ? 'false' : 'true'}
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-autocomplete="list"
          aria-expanded={showDropdown}
          aria-readonly={disabled}
          data-testid="rich-text-editor"
          data-placeholder={placeholder}
          className={`rich-text-box__editor w-full flex-1 min-h-0 overflow-y-auto whitespace-pre-wrap break-words bg-transparent text-sm text-foreground focus:outline-none pb-5 select-text ${
            disabled ? 'opacity-50' : ''
          } ${className}`}
          style={{ minHeight: `${minHeight}px`, maxHeight: `${maxHeight}px`, userSelect: 'text', WebkitUserSelect: 'text' }}
          onInput={disabled ? undefined : handleInput}
          onKeyDown={disabled ? undefined : handleKeyDown}
          onCopy={handleCopy}
          onCut={disabled ? undefined : handleCut}
          onPaste={disabled ? undefined : handlePaste}
          onCompositionStart={disabled ? undefined : handleCompositionStart}
          onCompositionEnd={disabled ? undefined : handleCompositionEnd}
        />

        <style>{`
          .rich-text-box__editor:empty::before {
            content: attr(data-placeholder);
            color: var(--text-muted, #6a6a8a);
            pointer-events: none;
          }
        `}</style>

        {/* Built-in character counter */}
        {maxLength > 0 && (
          <div
            className={`absolute bottom-0 right-0 text-[10px] tabular-nums bg-surface-2 pl-1 ${
              textLength >= maxLength ? 'text-red-400' : 'text-text-muted/60'
            }`}
            data-testid="rich-text-char-counter"
          >
            {textLength} / {maxLength}
          </div>
        )}
      </div>
    )
  }
)

const RichTextBox = memo(RichTextBoxInner)
RichTextBox.displayName = 'RichTextBox'

export { RichTextBox }
